import styled from "@emotion/styled";
import { useTranslation } from "next-i18next";
import React, { useMemo } from "react";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";

import { PublicMembershipGroupSerializer } from "@shared/modules/memberships/types";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";
import { breakpoints } from "@/styles/constants";

import DetailPageButton from "@/components/molecules/purchases/DetailPageButton";

import moment from "moment-timezone";
import {
  NestedPublicVenueSerializer,
  PublicBaseEventSerializer,
} from "@app-contexts/public/features/event-detail/data/types/events";
import { PublicVenueSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";
import { useTimer } from "@ui-kit/feedback/timer/useTimer";
import { UserBasedTicketBasketHoldsSerializer } from "@app-contexts/user/features/basket/data/types/basket";

const { lg } = breakpoints;

const StyledFlex = styled(Flex)`
  display: none;
  flex-direction: column;
  border-bottom: 1px solid var(--clr-paint-gray-400);
  background-color: var(--clr-font-light);
  width: 100%;
  height: auto;

  // Adds bottom padding for iOS devices so that page content remains within the phone's safe area
  // (example: iPhones with a notch)
  padding-bottom: env(safe-area-inset-bottom);

  position: sticky;
  z-index: var(--z-index-sticky);
  bottom: 0;

  @media only screen and (max-width: ${lg - 1}px) {
    display: flex;
  }
`;

interface CallToActionProps {
  eventData?: PublicBaseEventSerializer;
  membershipGroupData?: PublicMembershipGroupSerializer;
  venue: NestedPublicVenueSerializer | PublicVenueSerializer;
  openModal: () => void;
  holdDetailsData?: UserBasedTicketBasketHoldsSerializer;
  isSmallerThanSm: boolean;
}

/**
 * Footer with call to action button. Used on regular event detail pages and membership detail pages.
 * Displays a button that opens the ticket list modal or membership list modal.
 */
const CallToAction: React.FC<CallToActionProps> = ({
  venue,
  openModal,
  isSmallerThanSm,
  eventData = undefined,
  membershipGroupData = undefined,
  holdDetailsData = null,
}) => {
  const { t } = useTranslation("purchases");

  const priceRangeString = useMemo(() => {
    let priceRange = "";

    if (eventData) {
      priceRange = eventData?.no_ticket_types
        ? t("Free")
        : getPriceRange({
            items: eventData?.ticket_types,
            allInPricing: Boolean(eventData?.venue?.all_in_pricing_is_enabled),
            venueCurrency: eventData?.venue?.currency,
            shouldRound: false,
          });
    } else if (membershipGroupData) {
      priceRange = getPriceRange({
        items: membershipGroupData?.membership_levels,
        allInPricing: Boolean(venue?.all_in_pricing_is_enabled),
        venueCurrency: venue?.currency,
        shouldRound: false,
      });
    }

    return priceRange;
  }, [eventData, membershipGroupData, venue, t]);

  const hidePriceText = !priceRangeString;
  const eventSaleStartsOn = eventData?.event_sale_starts_on;
  const saleStartsOnTz = moment
    .tz(eventSaleStartsOn, eventData?.timezone)
    .toDate();
  const [timeRemaining] = useTimer(saleStartsOnTz);

  const saleStartsOnPassed = eventSaleStartsOn
    ? new Date().valueOf() >= new Date(eventSaleStartsOn).valueOf()
    : true;

  return (
    <StyledFlex>
      <Flex
        direction="row"
        p={{ base: "10px 20px", sm: "10px 40px" }}
        justifyContent="space-between"
        alignContent="center"
        alignItems="center"
        hidden={eventData?.no_ticket_types}
        display={eventSaleStartsOn && timeRemaining > 0 ? "none" : "flex"}
      >
        {!isSmallerThanSm && (
          <Flex direction="column" data-testid="price-range">
            <Text hidden={hidePriceText} fontSize="xs">
              {t("Price")}
            </Text>
            <Text hidden={hidePriceText} fontSize="sm" fontWeight="bold">
              {priceRangeString}
            </Text>
          </Flex>
        )}
        <DetailPageButton
          saleStartsOnPassed={saleStartsOnPassed}
          eventData={eventData}
          membershipGroupData={membershipGroupData}
          openModal={openModal}
          holdDetailsData={holdDetailsData}
          props={{
            "data-testid": "call-to-action-button",
            colorPalette: "secondary",
            minHeight: "50px",
            width: isSmallerThanSm ? "100%" : "50%",
            maxWidth: isSmallerThanSm ? "100%" : "364px",
            display: eventSaleStartsOn && timeRemaining > 0 ? "none" : "block",
          }}
        />
      </Flex>
    </StyledFlex>
  );
};

export default CallToAction;
